// Matrix — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Matrix Explanation modal. Same shape as mathBitDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version, using the multi-row array convention for grids.
// Three drills cross-reference curated problems instead of duplicating content.

const matrixDrills = [
  {
    id: 'transpose-matrix',
    title: 'Transpose Matrix',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Build a New Matrix',
        explanation: 'Allocate a brand new matrix with swapped dimensions, and copy every element across into its transposed position. Correct and simple, but uses O(n·m) extra space even when the input is square and could be transposed in place.',
        steps: [
          'Allocate a new matrix with rows and columns swapped relative to the input.',
          'For every cell [i][j] in the original, copy its value to [j][i] in the new matrix.',
          'Return the new matrix.',
          'The original matrix is left untouched, and a full second matrix is allocated.',
        ],
        code:
`int[][] transposeBasic(int[][] matrix) {
    int n = matrix.length, m = matrix[0].length;
    int[][] result = new int[m][n];
    for (int i = 0; i < n; i++) {
        for (int j = 0; j < m; j++) {
            result[j][i] = matrix[i][j];
        }
    }
    return result;
}`,
        complexity: { time: 'O(n · m)', space: 'O(n · m) — a full new matrix' },
      },
      {
        name: 'Optimized — In-Place Swap Across the Diagonal (Square Matrices)',
        explanation: 'For a SQUARE matrix, every pair of cells (i,j) and (j,i) can simply be swapped directly — no new matrix ever needs to be allocated. Only the cells strictly above the diagonal need visiting; each swap handles its mirrored partner below the diagonal too.',
        steps: [
          'For each row i, only consider columns j strictly greater than i (the upper triangle).',
          'Swap matrix[i][j] with matrix[j][i].',
          'The diagonal itself (i == j) never needs touching — it transposes to itself.',
          'After all upper-triangle swaps, the matrix is fully transposed in place.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 2, 3] },
            { label: 'row 1', values: [4, 5, 6] },
            { label: 'row 2', values: [7, 8, 9] },
          ],
          varNames: [],
          steps: [
            { snapshots: { 'row 0': [1, 4, 3], 'row 1': [2, 5, 6] }, note: 'Swap [0][1]=2 and [1][0]=4.' },
            { snapshots: { 'row 0': [1, 4, 7], 'row 2': [3, 8, 9] }, note: 'Swap [0][2]=3 and [2][0]=7.' },
            { snapshots: { 'row 1': [2, 5, 8], 'row 2': [3, 6, 9] }, note: 'Swap [1][2]=6 and [2][1]=8. Final: [[1,4,7],[2,5,8],[3,6,9]] — the transpose, done entirely in place.' },
          ],
        },
        code:
`void transposeInPlace(int[][] matrix) {
    int n = matrix.length;
    for (int i = 0; i < n; i++) {
        for (int j = i + 1; j < n; j++) {
            int temp = matrix[i][j];
            matrix[i][j] = matrix[j][i];
            matrix[j][i] = temp;
        }
    }
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'diagonal-traverse',
    title: 'Diagonal Traverse',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Group by Diagonal, Then Reverse Alternating Ones',
        explanation: 'Every cell (i, j) belongs to diagonal number i + j. Group all cells by their diagonal number first, then reverse every OTHER diagonal (since the zigzag direction alternates), and concatenate. Correct, but requires O(m·n) extra storage to hold every diagonal\'s group before the final pass.',
        steps: [
          'For each cell, compute its diagonal index as row + col, and append it to that diagonal\'s group.',
          'Once every cell is grouped, reverse the groups at EVEN diagonal indices (they need to read bottom-to-top).',
          'Concatenate every diagonal\'s (possibly reversed) group in order.',
          'Return the combined result.',
        ],
        code:
`int[] findDiagonalOrderBasic(int[][] matrix) {
    int m = matrix.length, n = matrix[0].length;
    List<List<Integer>> diagonals = new ArrayList<>();
    for (int d = 0; d < m + n - 1; d++) diagonals.add(new ArrayList<>());
    for (int i = 0; i < m; i++) {
        for (int j = 0; j < n; j++) {
            diagonals.get(i + j).add(matrix[i][j]);
        }
    }
    int[] result = new int[m * n];
    int idx = 0;
    for (int d = 0; d < diagonals.size(); d++) {
        List<Integer> diag = diagonals.get(d);
        if (d % 2 == 0) Collections.reverse(diag);
        for (int v : diag) result[idx++] = v;
    }
    return result;
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — the diagonal groupings' },
      },
      {
        name: 'Optimized — Simulate Directly, Bouncing Off Boundaries',
        explanation: 'Walk the diagonal directly, one cell at a time, tracking which direction is currently active. The moment a boundary (top row, bottom row, left column, or right column) is hit, flip direction and take a single step to move onto the next diagonal — no grouping storage needed.',
        steps: [
          'Track the current cell and whether the walk is currently heading "up-right" or "down-left."',
          'While heading up-right: if at the right edge, move down and switch direction; else if at the top edge, move right and switch direction; else move diagonally up-right.',
          'While heading down-left: if at the bottom edge, move right and switch direction; else if at the left edge, move down and switch direction; else move diagonally down-left.',
          'Repeat until every cell has been visited.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 2, 3] },
            { label: 'row 1', values: [4, 5, 6] },
            { label: 'row 2', values: [7, 8, 9] },
          ],
          varNames: ['order'],
          steps: [
            { highlights: { 'row 0': [0] }, vars: { order: '1' }, note: 'Start at [0][0]=1. Heading up-right, but at top edge — move right, switch to down-left.' },
            { highlights: { 'row 0': [1] }, vars: { order: '1,2' }, note: '[0][1]=2. Heading down-left: not at bottom or left edge — move to [1][0].' },
            { highlights: { 'row 1': [0] }, vars: { order: '1,2,4' }, note: '[1][0]=4. Heading down-left, at left edge — move down, switch to up-right.' },
            { highlights: { 'row 2': [0] }, vars: { order: '1,2,4,7' }, note: '[2][0]=7. Heading up-right: move to [1][1]. Continuing this bounce pattern for every remaining cell gives the final order: [1,2,4,7,5,3,6,8,9].' },
          ],
        },
        code:
`int[] findDiagonalOrder(int[][] matrix) {
    int m = matrix.length, n = matrix[0].length;
    int[] result = new int[m * n];
    int row = 0, col = 0, idx = 0;
    boolean goingUp = true;
    while (idx < m * n) {
        result[idx++] = matrix[row][col];
        if (goingUp) {
            if (col == n - 1) { row++; goingUp = false; }
            else if (row == 0) { col++; goingUp = false; }
            else { row--; col++; }
        } else {
            if (row == m - 1) { col++; goingUp = true; }
            else if (col == 0) { row++; goingUp = true; }
            else { row++; col--; }
        }
    }
    return result;
}`,
        complexity: { time: 'O(m · n)', space: 'O(1) extra beyond the output' },
      },
    ],
  },
  {
    id: 'spiral-matrix-drill',
    title: 'Spiral Matrix',
    level: 'Easy',
    crossReference:
      "This drill's full walkthrough — including a recursive layer-peeling alternative alongside the flagship " +
      "boundary-shrinking approach — lives in this pattern's curated problem list — open \"Spiral Matrix\" there " +
      "for the complete step-by-step. You can also try it hands-on in the \"Spiral Matrix\" mode of the " +
      "Interactive Practice playground.",
  },
  {
    id: 'set-matrix-zeroes-drill',
    title: 'Set Matrix Zeroes',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Set Matrix Zeroes\" there for the complete step-by-step. You can also try it hands-on in the " +
      "\"Set Matrix Zeroes\" mode of the Interactive Practice playground.",
  },
  {
    id: 'rotate-image-drill',
    title: 'Rotate Image',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Rotate Image\" there for the complete step-by-step. You can also try it hands-on in the \"Rotate Image\" " +
      "mode of the Interactive Practice playground.",
  },
  {
    id: 'search-a-2d-matrix',
    title: 'Search a 2D Matrix',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Scan Every Cell',
        explanation: 'Check every single cell in the matrix, one at a time, until the target is found or every cell has been checked. Correct, but ignores the fact that both rows and columns are sorted.',
        steps: [
          'For each row, and each column within that row:',
          'Compare the cell\'s value to the target.',
          'Return true the moment a match is found.',
          'If every cell is checked with no match, return false.',
        ],
        code:
`boolean searchMatrixBasic(int[][] matrix, int target) {
    for (int[] row : matrix) {
        for (int val : row) {
            if (val == target) return true;
        }
    }
    return false;
}`,
        complexity: { time: 'O(m · n)', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Binary Search on the Flattened Index Space',
        explanation: 'Since every row is sorted, and each row\'s first element is greater than the previous row\'s last element, the ENTIRE matrix reads as one single sorted sequence if flattened row by row. A single binary search over that virtual flattened array — converting each mid index back into (row, col) — finds the target in logarithmic time.',
        steps: [
          'Treat the matrix as a virtual 1D array of length rows × cols.',
          'Binary search over indices 0 to rows×cols − 1.',
          'For each mid index, convert it to (mid / cols, mid % cols) to read the actual cell.',
          'Narrow the search range based on the standard binary search comparison, exactly as with a real 1D array.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 3, 5, 7] },
            { label: 'row 1', values: [10, 11, 16, 20] },
            { label: 'row 2', values: [23, 30, 34, 60] },
          ],
          varNames: [],
          steps: [
            { highlights: { 'row 1': [1] }, note: 'mid=5 → row=5/4=1, col=5%4=1 → matrix[1][1]=11. 11 > 3 (target) — search the left half.' },
            { highlights: { 'row 0': [2] }, note: 'mid=2 → row=0, col=2 → matrix[0][2]=5. 5 > 3 — search further left.' },
            { highlights: { 'row 0': [0] }, note: 'mid=0 → row=0, col=0 → matrix[0][0]=1. 1 < 3 — search the right half.' },
            { highlights: { 'row 0': [1] }, note: 'mid=1 → row=0, col=1 → matrix[0][1]=3. Match! Target found — return true.' },
          ],
        },
        code:
`boolean searchMatrix(int[][] matrix, int target) {
    int m = matrix.length, n = matrix[0].length;
    int lo = 0, hi = m * n - 1;
    while (lo <= hi) {
        int mid = lo + (hi - lo) / 2;
        int val = matrix[mid / n][mid % n];
        if (val == target) return true;
        if (val < target) lo = mid + 1;
        else hi = mid - 1;
    }
    return false;
}`,
        complexity: { time: 'O(log(m · n))', space: 'O(1) extra' },
      },
    ],
  },
  {
    id: 'spiral-matrix-ii',
    title: 'Spiral Matrix II',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Generate Coordinates First, Fill Values Second',
        explanation: 'Generate the spiral ORDER of coordinates first (using the same boundary-shrinking traversal as reading a spiral, but just collecting positions instead of values), then make a second pass assigning 1, 2, 3, ... to those coordinates in order. Correct, but requires storing every coordinate before any value is placed.',
        steps: [
          'Traverse the boundaries in spiral order, but instead of reading values, record each (row, col) coordinate visited.',
          'Once every coordinate has been collected, make a second pass assigning increasing values 1, 2, 3, ... to them in the recorded order.',
          'Return the filled matrix.',
          'Two full passes are needed: one to determine order, one to fill values.',
        ],
        code:
`int[][] generateMatrixBasic(int n) {
    List<int[]> coords = new ArrayList<>();
    int top = 0, bottom = n - 1, left = 0, right = n - 1;
    while (top <= bottom && left <= right) {
        for (int j = left; j <= right; j++) coords.add(new int[]{top, j});
        top++;
        for (int i = top; i <= bottom; i++) coords.add(new int[]{i, right});
        right--;
        if (top <= bottom) { for (int j = right; j >= left; j--) coords.add(new int[]{bottom, j}); bottom--; }
        if (left <= right) { for (int i = bottom; i >= top; i--) coords.add(new int[]{i, left}); left++; }
    }
    int[][] matrix = new int[n][n];
    int val = 1;
    for (int[] c : coords) matrix[c[0]][c[1]] = val++;
    return matrix;
}`,
        complexity: { time: 'O(n²)', space: 'O(n²) — the coordinate list' },
      },
      {
        name: 'Optimized — Fill Values Directly During the Same Traversal',
        explanation: 'There\'s no need to separate "figure out the order" from "assign the values" — the exact same boundary-shrinking loop that reads a spiral can just as easily WRITE increasing values as it goes, in a single pass.',
        steps: [
          'Use the same four-boundary spiral traversal as Spiral Matrix.',
          'Instead of reading matrix[row][col] into a result list, WRITE the next increasing value into matrix[row][col] directly.',
          'Shrink the boundaries after each of the four directional sweeps, exactly as before.',
          'After the loop, the matrix is fully filled — no second pass needed.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 2, 3] },
            { label: 'row 1', values: [8, 9, 4] },
            { label: 'row 2', values: [7, 6, 5] },
          ],
          varNames: [],
          steps: [
            { note: 'Top row, left→right: write 1, 2, 3 into [0][0..2]. top++.' },
            { note: 'Right column, top→bottom: write 4, 5 into [1][2], [2][2]. right--.' },
            { note: 'Bottom row, right→left: write 6, 7 into [2][1], [2][0]. bottom--.' },
            { note: 'Left column, bottom→top: write 8 into [1][0]. left++. Only the center cell remains: write 9 into [1][1]. Final: [[1,2,3],[8,9,4],[7,6,5]].' },
          ],
        },
        code:
`int[][] generateMatrix(int n) {
    int[][] matrix = new int[n][n];
    int top = 0, bottom = n - 1, left = 0, right = n - 1;
    int val = 1;
    while (top <= bottom && left <= right) {
        for (int j = left; j <= right; j++) matrix[top][j] = val++;
        top++;
        for (int i = top; i <= bottom; i++) matrix[i][right] = val++;
        right--;
        if (top <= bottom) {
            for (int j = right; j >= left; j--) matrix[bottom][j] = val++;
            bottom--;
        }
        if (left <= right) {
            for (int i = bottom; i >= top; i--) matrix[i][left] = val++;
            left++;
        }
    }
    return matrix;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra beyond the output matrix' },
      },
    ],
  },
  {
    id: 'valid-sudoku',
    title: 'Valid Sudoku',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Three Separate Scans Per Cell',
        explanation: 'For each filled cell, independently scan its entire row, its entire column, and its 3×3 box, checking for a duplicate. Correct, but re-scans up to 27 other cells for every single filled cell, most of which get checked repeatedly across different cells\' scans.',
        steps: [
          'For each filled cell, scan the rest of its row for a duplicate.',
          'Scan the rest of its column for a duplicate.',
          'Scan the rest of its 3×3 box for a duplicate.',
          'Return false the moment any duplicate is found in any of these three scans.',
        ],
        code:
`boolean isValidSudokuBasic(char[][] board) {
    for (int r = 0; r < 9; r++) {
        for (int c = 0; c < 9; c++) {
            if (board[r][c] == '.') continue;
            char val = board[r][c];
            for (int c2 = 0; c2 < 9; c2++) {
                if (c2 != c && board[r][c2] == val) return false;
            }
            for (int r2 = 0; r2 < 9; r2++) {
                if (r2 != r && board[r2][c] == val) return false;
            }
            int boxRow = (r / 3) * 3, boxCol = (c / 3) * 3;
            for (int i = boxRow; i < boxRow + 3; i++) {
                for (int j = boxCol; j < boxCol + 3; j++) {
                    if ((i != r || j != c) && board[i][j] == val) return false;
                }
            }
        }
    }
    return true;
}`,
        complexity: { time: 'O(81 · 27) — fixed but with a heavy constant factor', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — Single Pass With Row/Column/Box Keys',
        explanation: 'Visit every cell exactly ONCE. For each filled cell, build three unique keys — one for "this digit in this row," one for "this digit in this column," one for "this digit in this box" — and track them all in a single set. Any key collision means a duplicate was found.',
        steps: [
          'For each filled cell, compute a row-key, a column-key, and a box-key that uniquely identify "this digit belongs here."',
          'Try to add all three keys to a shared set.',
          'If any key is already present, a duplicate exists in that row, column, or box — return false immediately.',
          'If every cell is processed with no collisions, return true.',
        ],
        visual: {
          arrays: [{ label: 'row 0 (excerpt): 5, 3, ., ., 7, ., ., ., .', values: [5, 3, '·', '·', 7, '·', '·', '·', '·'] }],
          varNames: [],
          steps: [
            { highlights: { 'row 0 (excerpt): 5, 3, ., ., 7, ., ., ., .': [0] }, note: `Cell (0,0)='5': keys "row0-5", "col0-5", "box0-0-5" — none seen before, add all three.` },
            { highlights: { 'row 0 (excerpt): 5, 3, ., ., 7, ., ., ., .': [1] }, note: `Cell (0,1)='3': keys "row0-3", "col1-3", "box0-0-3" — none seen, add all three.` },
            { note: `If a LATER cell in the same box (say, (1,1)) also held '5', its box-key "box0-0-5" would already be in the set — an instant duplicate detection, without rescanning the whole box.` },
          ],
        },
        code:
`boolean isValidSudoku(char[][] board) {
    Set<String> seen = new HashSet<>();
    for (int r = 0; r < 9; r++) {
        for (int c = 0; c < 9; c++) {
            char val = board[r][c];
            if (val == '.') continue;
            String rowKey = "row" + r + val;
            String colKey = "col" + c + val;
            String boxKey = "box" + (r / 3) + "-" + (c / 3) + val;
            if (!seen.add(rowKey) || !seen.add(colKey) || !seen.add(boxKey)) {
                return false;
            }
        }
    }
    return true;
}`,
        complexity: { time: 'O(81) = O(1) — fixed board size, single pass', space: 'O(81) for the set, in the worst case' },
      },
    ],
  },
  {
    id: 'game-of-life',
    title: 'Game of Life',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Compute Next State Using a Full Copy',
        explanation: 'Make a complete copy of the board first. Compute every cell\'s next state by reading NEIGHBOR values only from the untouched copy, and write results into the original board. Correct, and clear to reason about, but uses a full second board\'s worth of memory.',
        steps: [
          'Copy the entire board before making any changes.',
          'For each cell, count its live neighbors by reading from the COPY (never the board being mutated).',
          'Apply the standard rules (a live cell survives with 2-3 neighbors, a dead cell becomes alive with exactly 3) to decide the new state.',
          'Write the new state into the original board.',
        ],
        code:
`void gameOfLifeBasic(int[][] board) {
    int m = board.length, n = board[0].length;
    int[][] copy = new int[m][n];
    for (int i = 0; i < m; i++) copy[i] = board[i].clone();

    for (int r = 0; r < m; r++) {
        for (int c = 0; c < n; c++) {
            int liveNeighbors = countLiveNeighbors(copy, r, c);
            if (copy[r][c] == 1) {
                board[r][c] = (liveNeighbors == 2 || liveNeighbors == 3) ? 1 : 0;
            } else {
                board[r][c] = (liveNeighbors == 3) ? 1 : 0;
            }
        }
    }
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — the full board copy' },
      },
      {
        name: 'Optimized — Encode Old and New State in the Same Cell (O(1) Space)',
        explanation: 'Store BOTH the old and new state in a single cell using two bits: bit 0 holds the original state, bit 1 will hold the new state. Since neighbor-counting only ever reads bit 0 (via & 1), it keeps seeing the ORIGINAL state even for cells already updated — no separate copy is needed. A final pass shifts every cell from its 2-bit encoding down to just the new state.',
        steps: [
          'For each cell, count live neighbors by checking (board[nr][nc] & 1) == 1 — this always reads the ORIGINAL state, regardless of whether bit 1 has been set yet.',
          'Based on the standard rules, if the cell should become (or remain) alive, set bit 1 with board[r][c] |= 2 — leaving bit 0 (the old state) untouched.',
          'After every cell has been processed this way, make one final pass shifting every cell right by 1 (board[r][c] >>= 1), moving the new state down into bit 0.',
          'The board now holds only the new state, with zero extra memory used beyond the input itself.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [0, 1, 0] },
            { label: 'row 1', values: [0, 0, 1] },
          ],
          varNames: [],
          steps: [
            { note: `Cell (0,1)=1 (old bit0=1). Its live neighbors: only (1,2)=1 → count=1. Alive with <2 neighbors → dies: bit 1 stays 0, so the cell remains binary 01.` },
            { note: `Cell (1,0)=0 (old bit0=0). Its live neighbors: (0,1)=1 counts, plus others from the full board → count=3. Dead with exactly 3 → becomes alive: board[1][0] |= 2, making it binary 10.` },
            { note: `Final pass: every cell is shifted right by 1. Binary 01 (was alive, now dead) becomes 0. Binary 10 (was dead, now alive) becomes 1 — exactly the new state, computed with zero extra board allocated.` },
          ],
        },
        code:
`void gameOfLife(int[][] board) {
    int m = board.length, n = board[0].length;
    int[] dr = {-1,-1,-1,0,0,1,1,1};
    int[] dc = {-1,0,1,-1,1,-1,0,1};

    for (int r = 0; r < m; r++) {
        for (int c = 0; c < n; c++) {
            int liveNeighbors = 0;
            for (int k = 0; k < 8; k++) {
                int nr = r + dr[k], nc = c + dc[k];
                if (nr >= 0 && nr < m && nc >= 0 && nc < n && (board[nr][nc] & 1) == 1) {
                    liveNeighbors++;
                }
            }
            if ((board[r][c] & 1) == 1) {
                if (liveNeighbors == 2 || liveNeighbors == 3) board[r][c] |= 2;
            } else {
                if (liveNeighbors == 3) board[r][c] |= 2;
            }
        }
    }
    for (int r = 0; r < m; r++) {
        for (int c = 0; c < n; c++) {
            board[r][c] >>= 1;
        }
    }
}`,
        complexity: { time: 'O(m · n)', space: 'O(1) extra' },
      },
    ],
  },
];

export default matrixDrills;
